import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Ban,
  BookOpen,
  Crown,
  Briefcase,
  CheckCircle2,
  Download,
  Eye,
  GraduationCap,
  IndianRupee,
  MoreVertical,
  PencilLine,
  Plus,
  RotateCcw,
  Search,
  UserCheck,
  Users,
  UserX,
} from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingBlock } from '@/components/ui/Loader';
import { Menu } from '@/components/ui/Menu';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { TEACHING_ROLES } from '@/constants/roles';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { instituteApi } from '@/features/institute/api/institute.api';
import { BulkImportSteps } from '@/features/institute/admissions/components/BulkImportPanel';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { capitalizeWords, formatCurrency, formatDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { MonthPayrollSummary } from '@/types/payroll';
import type { StaffClassAllotment, StaffListParams, StaffMember, StaffStatus } from '@/types/staff';
import { staffApi } from '../api/staff.api';
import { StaffIdentity } from '../components/StaffIdentity';
import { LessonNowNext } from '../components/LessonNowNext';
import { StaffExportDialog } from '../components/StaffExportDialog';
import { payrollApi } from '@/features/institute/payroll/api/payroll.api';
import { describeIncrement } from '@/features/institute/payroll/format';
import { STAFF_IMPORT } from '../staffImport';
import { setStaffStatus } from '../staffStatus';
import { useRefetchAtNextBell } from '../useRefetchAtNextBell';
import { announceAllotmentSaved, ClassAllotmentModal } from '../components/ClassAllotmentModal';
import { StaffFormModal } from '../components/StaffFormModal';
import { StaffProfilePage, type StaffProfileTab } from '../components/StaffProfilePage';
import {
  NON_TEACHING_FILTER,
  STAFF_PER_PAGE,
  STAFF_ROLE_FILTER_OPTIONS,
  STAFF_STATUS_FILTER_OPTIONS,
  STAFF_STATUS_LABEL,
  STAFF_STATUS_TONE,
} from '../constants';

/**
 * The register's filters, as they sit in the address bar.
 *
 * `nonTeaching` is the request's `true | undefined` flag written as text, since
 * an address holds only text: `'true'` or blank.
 */
type Filters = {
  page: number;
  search: string;
  staffType: string;
  subjectUuid: string;
  status: StaffStatus | '';
  nonTeaching: '' | 'true';
};

const DEFAULT_FILTERS: Filters = {
  page: 1,
  search: '',
  staffType: '',
  subjectUuid: '',
  status: '',
  nonTeaching: '',
};

/**
 * What a link may set. Non-teaching is a group rather than a type, so it is
 * kept out of `staffType` here exactly as the role filter keeps it out.
 */
const FILTER_OPTIONS = {
  allowed: {
    staffType: STAFF_ROLE_FILTER_OPTIONS.map((option) => option.value).filter(
      (value) => value !== NON_TEACHING_FILTER,
    ),
    status: Object.keys(STAFF_STATUS_LABEL),
    nonTeaching: ['true'],
  },
};

/** The keys of a profile opened by link, which closing it takes back out of the address. */
const PROFILE_LINK_KEYS = ['profile', 'tab', 'from'];

/** The address without the profile link, its filters left in place. */
function withoutProfileLink(current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const key of PROFILE_LINK_KEYS) next.delete(key);
  return next;
}

export function StaffPage() {
  const permissions = useAuthStore((state) => state.user?.permissions);
  const canManage = Boolean(permissions?.includes('STAFF_MANAGE'));
  // Salaries ride on the list only for this key, so without it their columns go.
  const canViewPayroll = Boolean(permissions?.includes('PAYROLL_VIEW'));

  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  // Seeded from the address, so a restored search is shown in the box too.
  const [searchDraft, setSearchDraft] = useState(filters.search);
  // The request's shape: the flag back to `true | undefined`, the page size added.
  const listParams: StaffListParams = {
    ...filters,
    limit: STAFF_PER_PAGE,
    nonTeaching: filters.nonTeaching ? true : undefined,
  };
  const [editing, setEditing] = useState<StaffMember | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [viewingProfileMember, setViewingProfileMember] = useState<StaffMember | null>(null);
  const [editingFromProfile, setEditingFromProfile] = useState<StaffMember | null>(null);
  const [profileTab, setProfileTab] = useState<StaffProfileTab>('Overview');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedProfileUuid = searchParams.get('profile');
  const linkedProfileTab = searchParams.get('tab');
  const isFromAttendance =
    searchParams.get('from') === 'attendance' ||
    (location.state as { from?: string } | null)?.from === '/admin/staff-attendance';
  const [isLoadingProfile, setIsLoadingProfile] = useState(Boolean(linkedProfileUuid));
  const [isImporting, setIsImporting] = useState(false);
  const [allotting, setAllotting] = useState<StaffMember | null>(null);
  /** The account whose status is being switched, awaiting confirmation. */
  const [statusTarget, setStatusTarget] = useState<StaffMember | null>(null);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [invitingUuid, setInvitingUuid] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => staffApi.list(listParams),
    [
      filters.page,
      filters.search,
      filters.staffType,
      filters.subjectUuid,
      filters.status,
      filters.nonTeaching,
    ],
  );
  // The Now / Next column follows the timetable through the day.
  useRefetchAtNextBell(data?.items, refetch);

  const {
    data: summary,
    isLoading: isSummaryLoading,
    refetch: refetchSummary,
  } = useApiQuery(() => staffApi.summary(), []);

  const { data: roles } = useApiQuery(() => instituteApi.listRoles(), []);

  // The month's totals are read only for a viewer who may see salaries at all.
  const monthPayroll = useApiQuery(
    () => (canViewPayroll ? payrollApi.getMonthSummary() : Promise.resolve(null)),
    [canViewPayroll],
  );

  /*
    `?profile=<uuid>&tab=Attendance` opens one person's profile directly — how
    the punch-ins register sends the office to somebody's history. The record is
    fetched by uuid rather than looked up in the loaded page, because the person
    may be on any page of the register, or filtered out of it.
  */
  useEffect(() => {
    if (!linkedProfileUuid) {
      setIsLoadingProfile(false);
      return;
    }
    let isActive = true;
    setIsLoadingProfile(true);

    staffApi
      .get(linkedProfileUuid)
      .then((member) => {
        if (!isActive) return;
        setProfileTab(linkedProfileTab === 'Attendance' ? 'Attendance' : 'Overview');
        setViewingProfileMember(member);
      })
      .catch((caught: unknown) => {
        if (!isActive) return;
        toast.error(
          'Could not open that profile',
          caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
        );
        setSearchParams(withoutProfileLink, { replace: true });
      })
      .finally(() => {
        if (isActive) setIsLoadingProfile(false);
      });

    return () => {
      isActive = false;
    };
  }, [linkedProfileUuid, linkedProfileTab, setSearchParams]);

  function closeProfile() {
    setViewingProfileMember(null);
    setProfileTab('Overview');
    // Only the link's own keys go — the register's filters stay in the address.
    if (linkedProfileUuid) setSearchParams(withoutProfileLink, { replace: true });
  }

  function reload() {
    refetch();
    refetchSummary();
  }

  function updateFilters(patch: Partial<Filters>) {
    setFilters({ ...patch, ...(patch.page ? {} : { page: 1 }) });
  }

  function handleSearchSubmit(e: FormEvent) {
    e.preventDefault();
    updateFilters({ search: searchDraft.trim() });
  }

  /*
    The stat cards double as quick filters. Role cards set only the role and
    status cards only the status, so "Teachers" then "Active" narrows to active
    teachers — the same as picking both from the dropdowns below.
  */
  const roleCleared: Partial<Filters> = {
    staffType: '',
    nonTeaching: '',
    subjectUuid: '',
  };
  const isAllStaff = !filters.staffType && !filters.nonTeaching && !filters.status;

  function handleResetFilters() {
    setSearchDraft('');
    setFilters(DEFAULT_FILTERS);
  }

  function openForm(member: StaffMember | null) {
    setEditing(member);
    setIsFormOpen(true);
  }

  function handleSaved(saved: StaffMember, wasCreated: boolean) {
    setIsFormOpen(false);
    setEditing(null);
    if (editingFromProfile) {
      setViewingProfileMember(saved);
      setEditingFromProfile(null);
    }
    toast.success(
      wasCreated ? `${saved.fullName} has been added` : `${saved.fullName}'s account is updated`,
      wasCreated
        ? `An email is on its way to ${saved.email} with a link to set their password.`
        : undefined,
    );
    reload();
  }

  /**
   * Suspends an account, or reinstates a suspended one.
   *
   * One word for it everywhere — "Suspend", with "Reactivate" as the way back,
   * as for students and classes — so the register, the profile and the edit
   * form never disagree.
   *
   * A status change, never a removal: the person stays on the register and in
   * the Suspended count, keeps their classes, and can be reinstated. It once
   * called `staffApi.remove`, which deleted the account — so a suspended member
   * of staff vanished from the list and from every count above it.
   */
  async function confirmStatusChange() {
    if (!statusTarget) return;
    const deactivating = statusTarget.status === 'ACTIVE';
    setIsChangingStatus(true);

    try {
      const outcome = await setStaffStatus(statusTarget, deactivating ? 'INACTIVE' : 'ACTIVE');
      setStatusTarget(null);
      if (!outcome.ok) {
        toast.error(
          'This record needs correcting first',
          'Open Edit staff member, fix the highlighted details and save, then change the status.',
        );
        return;
      }
      toast.success(
        deactivating
          ? `${outcome.saved.fullName} has been suspended`
          : `${outcome.saved.fullName} has been reactivated`,
        deactivating ? 'They are signed out now and cannot sign in until reactivated.' : undefined,
      );
      reload();
    } catch (caught) {
      toast.error(
        deactivating ? 'Could not suspend this account' : 'Could not reactivate this account',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsChangingStatus(false);
    }
  }

  async function resendInvite(member: StaffMember) {
    setInvitingUuid(member.uuid);
    try {
      await staffApi.resendInvite(member.uuid);
      toast.success(
        'A fresh link is on its way',
        `${member.email} can set a password with it. Any earlier link has stopped working.`,
      );
    } catch (caught) {
      toast.error(
        'Could not send that invite',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setInvitingUuid(null);
    }
  }

  const staffList = data?.items ?? [];

  const totalStaff = summary?.total ?? data?.meta.total ?? 0;
  const teachersCount = summary?.byType.TEACHER ?? 0;
  const nonTeachersCount = Math.max(0, totalStaff - teachersCount);
  const activeCount = summary?.byStatus.ACTIVE ?? 0;
  /*
    Active means "may sign in", not "has": an account still on its invite has
    never signed in. The pending count spans every status, so it is named as
    its own figure rather than taken off the active one.
  */
  const awaitingInviteCount = summary?.invitesPending ?? 0;
  // Both kinds of switched-off account, added: `??` only ever read one of them.
  const inactiveCount = (summary?.byStatus.INACTIVE ?? 0) + (summary?.byStatus.SUSPENDED ?? 0);

  if (isFormOpen) {
    return (
      <StaffFormModal
        member={editing}
        isOpen={isFormOpen}
        roles={roles ?? []}
        onClose={() => {
          setIsFormOpen(false);
          setEditing(null);
          if (editingFromProfile) {
            setViewingProfileMember(editingFromProfile);
            setEditingFromProfile(null);
          }
        }}
        onSaved={handleSaved}
      />
    );
  }

  if (linkedProfileUuid && isLoadingProfile && !viewingProfileMember) {
    return (
      <div className="w-full flex items-center justify-center min-h-[60vh]">
        <LoadingBlock label="Loading staff profile…" className="py-20" />
      </div>
    );
  }

  if (viewingProfileMember) {
    return (
      <StaffProfilePage
        member={viewingProfileMember}
        initialTab={profileTab}
        roles={roles ?? undefined}
        onBack={() => {
          if (isFromAttendance) {
            navigate('/admin/staff-attendance');
            return;
          }
          closeProfile();
        }}
        backLabel={isFromAttendance ? 'Staff Attendance' : 'Staff'}
        canManage={canManage}
        onChanged={(saved) => {
          setViewingProfileMember(saved);
          reload();
        }}
        onEdit={(m) => {
          setEditingFromProfile(m);
          setViewingProfileMember(null);
          openForm(m);
        }}
      />
    );
  }

  return (
    <div className="w-full flex flex-col gap-6 pb-12 transition-colors">
      {/* Top Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            Staff Directory
          </h1>
          <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400 mt-0.5">
            Manage staff members, roles, departments, payroll and accounts.
          </p>
        </div>

        {canManage && (
          <div className="flex items-center gap-3 flex-wrap">
            {/* Sequence: Import -> Export -> Add Staff Member */}
            {/* 1. Import */}
            <button
              type="button"
              onClick={() => setIsImporting(true)}
              className="rounded-xl bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-[#16253d] text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white px-4 py-2.5 border border-slate-200 dark:border-slate-800 text-sm font-bold inline-flex items-center gap-2 transition-all cursor-pointer shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <Download className="h-4 w-4 text-slate-500" />
              <span>Import</span>
            </button>

            {/* 2. Export */}
            <StaffExportDialog
              pageFilters={listParams}
              triggerClassName="rounded-xl bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-[#16253d] text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white px-4 py-2.5 border border-slate-200 dark:border-slate-800 text-sm font-bold transition-all cursor-pointer shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            />

            {/* 3. Add Staff Member */}
            <button
              type="button"
              onClick={() => openForm(null)}
              className="rounded-xl bg-indigo-600 tracking-wide hover:bg-indigo-700 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-2 transition-all cursor-pointer shadow-sm shadow-indigo-500/20 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              <Plus className="h-4 w-4 stroke-[2.5]" />
              <span>Add Staff Member</span>
            </button>
          </div>
        )}
      </div>

      {/* 6 Metric Stat Cards Grid with Image Visual Design */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-[1fr_1fr_1fr_1fr_1fr_1.45fr] gap-2.5">
        {/* Card 1: TOTAL STAFF */}
        <button
          type="button"
          onClick={() => updateFilters({ ...roleCleared, status: '' })}
          aria-pressed={isAllStaff}
          className={cn(
            "bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-200/80 dark:border-indigo-900/50 rounded-2xl p-3.5 flex items-start justify-between shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 w-full text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500",
            isAllStaff && "!border-indigo-500 dark:!border-indigo-400 ring-1 ring-inset ring-indigo-500/30",
          )}
        >
          <div>
            <span className="text-[11px] font-extrabold tracking-wider uppercase text-indigo-700 dark:text-indigo-300">
              TOTAL STAFF
            </span>
            <div className={cn("text-2xl sm:text-2xl font-black text-slate-900 dark:text-white mt-1", isSummaryLoading && "animate-pulse")}>
              {totalStaff}
            </div>
            <span className="text-[11px] font-medium text-indigo-600/80 dark:text-indigo-400/80 mt-0.5 block truncate">
              All staff members
            </span>
          </div>
          <div className="w-9 h-9 rounded-2xl bg-indigo-600 dark:bg-indigo-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-indigo-600/30">
            <Users className="w-4.5 h-4.5" />
          </div>
        </button>

        {/* Card 2: TEACHERS */}
        <button
          type="button"
          onClick={() => updateFilters({ ...roleCleared, staffType: 'TEACHER' })}
          aria-pressed={filters.staffType === 'TEACHER'}
          className={cn(
            "bg-cyan-50/60 dark:bg-cyan-950/30 border border-cyan-200/80 dark:border-cyan-900/50 rounded-2xl p-3.5 flex items-start justify-between shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 w-full text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-500",
            filters.staffType === 'TEACHER' && "!border-cyan-500 dark:!border-cyan-400 ring-1 ring-inset ring-cyan-500/30",
          )}
        >
          <div>
            <span className="text-[11px] font-extrabold tracking-wider uppercase text-cyan-800 dark:text-cyan-300">
              TEACHERS
            </span>
            <div className={cn("text-2xl sm:text-2xl font-black text-slate-900 dark:text-white mt-1", isSummaryLoading && "animate-pulse")}>
              {teachersCount}
            </div>
            <span className="text-[11px] font-medium text-cyan-700/80 dark:text-cyan-400/80 mt-0.5 block truncate">
              Teaching staff
            </span>
          </div>
          <div className="w-9 h-9 rounded-2xl bg-cyan-600 dark:bg-cyan-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-cyan-600/30">
            <GraduationCap className="w-4.5 h-4.5" />
          </div>
        </button>

        {/* Card 3: NON-TEACHING */}
        <button
          type="button"
          onClick={() => updateFilters({ ...roleCleared, nonTeaching: 'true' })}
          aria-pressed={Boolean(filters.nonTeaching)}
          className={cn(
            "bg-purple-50/60 dark:bg-purple-950/30 border border-purple-200/80 dark:border-purple-900/50 rounded-2xl p-3.5 flex items-start justify-between shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 w-full text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-500",
            Boolean(filters.nonTeaching) && "!border-purple-500 dark:!border-purple-400 ring-1 ring-inset ring-purple-500/30",
          )}
        >
          <div>
            <span className="text-[11px] font-extrabold tracking-wider uppercase text-purple-800 dark:text-purple-300">
              NON-TEACHING
            </span>
            <div className={cn("text-2xl sm:text-2xl font-black text-slate-900 dark:text-white mt-1", isSummaryLoading && "animate-pulse")}>
              {nonTeachersCount}
            </div>
            <span className="text-[11px] font-medium text-purple-700/80 dark:text-purple-400/80 mt-0.5 block truncate">
              Admin & support
            </span>
          </div>
          <div className="w-9 h-9 rounded-2xl bg-purple-600 dark:bg-purple-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-purple-600/30">
            <Briefcase className="w-4.5 h-4.5" />
          </div>
        </button>

        {/* Card 4: ACTIVE */}
        <button
          type="button"
          onClick={() => updateFilters({ status: 'ACTIVE' })}
          aria-pressed={filters.status === 'ACTIVE'}
          className={cn(
            "bg-emerald-50/60 dark:bg-emerald-950/30 border border-emerald-200/80 dark:border-emerald-900/50 rounded-2xl p-3.5 flex items-start justify-between shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 w-full text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500",
            filters.status === 'ACTIVE' && "!border-emerald-500 dark:!border-emerald-400 ring-1 ring-inset ring-emerald-500/30",
          )}
        >
          <div>
            <span className="text-[11px] font-extrabold tracking-wider uppercase text-emerald-800 dark:text-emerald-300">
              ACTIVE
            </span>
            <div className={cn("text-2xl sm:text-2xl font-black text-slate-900 dark:text-white mt-1", isSummaryLoading && "animate-pulse")}>
              {activeCount}
            </div>
            <span className="text-[11px] font-medium text-emerald-700/80 dark:text-emerald-400/80 mt-0.5 block truncate">
              {awaitingInviteCount > 0
                ? `${awaitingInviteCount} ${awaitingInviteCount === 1 ? 'invite' : 'invites'} pending`
                : 'Can sign in'}
            </span>
          </div>
          <div className="w-9 h-9 rounded-2xl bg-emerald-600 dark:bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-emerald-600/30">
            <CheckCircle2 className="w-4.5 h-4.5" />
          </div>
        </button>

        {/* Card 5: INACTIVE. The server reads the INACTIVE filter as every
            switched-off account, SUSPENDED included (`staffListWhere`), so the
            list this opens holds exactly the two statuses counted here. */}
        <button
          type="button"
          onClick={() => updateFilters({ status: 'INACTIVE' })}
          aria-pressed={filters.status === 'INACTIVE'}
          className={cn(
            "bg-rose-50/60 dark:bg-rose-950/30 border border-rose-200/80 dark:border-rose-900/50 rounded-2xl p-3.5 flex items-start justify-between shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 w-full text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-rose-500",
            filters.status === 'INACTIVE' && "!border-rose-500 dark:!border-rose-400 ring-1 ring-inset ring-rose-500/30",
          )}
        >
          <div>
            <span className="text-[11px] font-extrabold tracking-wider uppercase text-rose-800 dark:text-rose-300">
              SUSPENDED
            </span>
            <div className={cn("text-2xl sm:text-2xl font-black text-slate-900 dark:text-white mt-1", isSummaryLoading && "animate-pulse")}>
              {inactiveCount}
            </div>
            <span className="text-[11px] font-medium text-rose-700/80 dark:text-rose-400/80 mt-0.5 block truncate">
              Cannot sign in
            </span>
          </div>
          <div className="w-9 h-9 rounded-2xl bg-rose-600 dark:bg-rose-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-rose-600/30">
            <UserX className="w-4.5 h-4.5" />
          </div>
        </button>

        {/* Card 6: TOTAL PAYROLL */}
        <button
          type="button"
          onClick={() => navigate('/admin/payroll')}
          className="col-span-2 sm:col-span-1 lg:col-span-1 bg-teal-50/60 dark:bg-teal-950/30 border border-teal-200/80 dark:border-teal-900/50 rounded-2xl p-3.5 flex items-start justify-between gap-2 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 w-full text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500"
        >
          <div className="min-w-0 flex-1">
            <span className="text-[11px] font-extrabold tracking-wider uppercase text-teal-800 dark:text-teal-300">
              TOTAL PAYROLL 
            </span><span className="text-sm font-normal text-teal-700 dark:text-teal-500 pl-1">( This month )</span>
            <MonthPayrollFigures
              canView={canViewPayroll}
              summary={monthPayroll.data}
              isLoading={monthPayroll.isLoading}
              hasError={Boolean(monthPayroll.error)}
            />
          </div>
          <div className="w-9 h-9 rounded-2xl bg-teal-600 dark:bg-teal-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-teal-600/30">
            <IndianRupee className="w-4.5 h-4.5" />
          </div>
        </button>
      </div>

      {/* Control Filter Bar */}
      <section
        aria-label="Filter staff"
        className="rounded-2xl p-2.5 bg-slate-50 dark:bg-[#0b1322] border border-slate-200/90 dark:border-slate-800 shadow-xs flex flex-col gap-2.5"
      >
        <form onSubmit={handleSearchSubmit} className="flex flex-wrap items-center gap-2">
          {/* Search Bar */}
          <div className="relative w-full sm:w-auto sm:flex-1 min-w-0 sm:min-w-[200px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search staff name, email, employee code..."
              value={searchDraft}
              onChange={(e) => {
                setSearchDraft(e.target.value);
                updateFilters({ search: e.target.value.trim() });
              }}
              className="!h-9 !py-1 !pl-9 !pr-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors w-full focus:outline-none placeholder:text-slate-400"
            />
          </div>

          {/* Role / Teaching Filter */}
          <div className="w-[calc(50%-0.25rem)] sm:w-36 shrink-0">
            <Select
              label="Role"
              hideLabel
              value={filters.nonTeaching ? NON_TEACHING_FILTER : filters.staffType || ''}
              options={[{ value: '', label: 'All Roles' }, ...STAFF_ROLE_FILTER_OPTIONS]}
              // A subject means something only for a teacher, so it is
              // dropped the moment the role is anything else. Non-teaching is a
              // group rather than a type, so it travels as its own flag.
              onChange={(e) => {
                const isNonTeaching = e.target.value === NON_TEACHING_FILTER;
                updateFilters({
                  staffType: isNonTeaching ? '' : e.target.value,
                  nonTeaching: isNonTeaching ? 'true' : '',
                  ...(e.target.value === 'TEACHER' ? {} : { subjectUuid: '' }),
                });
              }}
              className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            />
          </div>

          {/* Department — the subjects teachers are allotted. Only for the
              Teacher role: nobody else is allotted a subject to filter by. */}
          <div className="w-[calc(50%-0.25rem)] sm:w-40 shrink-0">
            <Select
              label="Department"
              hideLabel
              value={filters.subjectUuid}
              options={[
                { value: '', label: 'All Departments' },
                ...(summary?.subjects ?? []).map((subject) => ({
                  value: subject.uuid,
                  label: capitalizeWords(subject.name),
                })),
              ]}
              disabled={filters.staffType !== 'TEACHER'}
              title={
                filters.staffType === 'TEACHER'
                  ? 'Filter teachers by the subject they teach'
                  : 'Choose Teacher as the role to filter by subject'
              }
              onChange={(e) => updateFilters({ subjectUuid: e.target.value })}
              className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            />
          </div>

          {/* Status Filter */}
          <div className="w-[calc(50%-0.25rem)] sm:w-36 shrink-0">
            <Select
              label="Status"
              hideLabel
              value={filters.status || ''}
              options={[
                { value: '', label: 'All Statuses' },
                ...STAFF_STATUS_FILTER_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
              ]}
              onChange={(e) => updateFilters({ status: e.target.value as StaffStatus | '' })}
              className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            />
          </div>

          {/* Clear Button */}
          <button
            type="button"
            onClick={handleResetFilters}
            className="!h-9 !py-1 !px-3 !text-[13px] font-bold border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-200 hover:bg-slate-50 hover:text-rose-600 hover:border-rose-300 shadow-2xs rounded-xl flex items-center gap-1.5 shrink-0 transition-colors cursor-pointer"
          >
            Clear
          </button>
        </form>
      </section>

      {error && <QueryError title="Could not load staff list" error={error} onRetry={reload} />}

      {/* Main Full-Width Table Container with Shadow */}
      <div className="relative z-0 bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl overflow-hidden shadow-[0_10px_30px_-5px_rgba(0,0,0,0.08)] dark:shadow-[0_10px_30px_-5px_rgba(0,0,0,0.4)] transition-all">
        {isLoading ? (
          <div className="p-12 text-center text-slate-500 dark:text-slate-400 text-sm">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mb-2"></div>
            <p>Loading staff directory...</p>
          </div>
        ) : staffList.length === 0 ? (
          <EmptyState
            title="No staff members found"
            description="No staff records match your current filters. Try resetting the filters or add a new staff member."
            action={
              <button
                type="button"
                onClick={handleResetFilters}
                className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-xs font-semibold shadow-md shadow-emerald-600/20 hover:bg-emerald-700 transition-all cursor-pointer"
              >
                Clear Filters
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto w-full table-scrollbar">
            <table className="w-full text-left border-collapse min-w-[850px]">
              <thead>
                <tr className="bg-slate-100 dark:bg-[#0b1322] border-b border-slate-200 dark:border-slate-800 text-[14px] font-bold tracking-wide text-slate-700 dark:text-slate-300 align-middle">
                  {/* Single Combined Sticky Staff Member Column (Name + Email + Code + Contact + Dept + Role) */}
                  <th className="py-3.5 px-4 sm:sticky sm:left-0 z-20 bg-slate-100 dark:bg-[#0b1322] min-w-[260px] whitespace-nowrap sm:border-r border-slate-200 dark:border-slate-800 sm:shadow-2xs">
                    Staff member
                  </th>
                  <th className="py-3.5 px-4 min-w-[200px] whitespace-nowrap">Address</th>
                  <th className="py-3.5 px-4 min-w-[220px] whitespace-nowrap">Classes &amp; subjects</th>
                  <th className="py-3.5 px-4 min-w-[190px] whitespace-nowrap">Now / Next class</th>
                  {canViewPayroll && (
                    <th className="py-3.5 px-4 min-w-[170px] whitespace-nowrap">Salary (monthly)</th>
                  )}
                  <th className="py-3.5 px-4 min-w-[150px] text-center">
                    <div className="text-[14px] font-bold tracking-wide text-slate-700 dark:text-slate-300 mb-1 whitespace-nowrap">
                      Leaves
                    </div>
                    <div className="grid grid-cols-2 text-xs font-bold text-slate-600 dark:text-slate-300 border-t border-slate-200 dark:border-slate-800 pt-1 px-1 gap-1 text-center">
                      <span className="whitespace-nowrap">This Month</span>
                      <span className="whitespace-nowrap">Session</span>
                    </div>
                  </th>
                  {canViewPayroll && (
                    <th className="py-3.5 px-4 min-w-[130px] whitespace-nowrap">Last Increment</th>
                  )}
                  <th className="py-3.5 px-4 min-w-[120px] whitespace-nowrap">Status</th>
                  <th className="py-3.5 px-4 text-right sm:sticky sm:right-0 z-20 bg-slate-100 dark:bg-[#0b1322] min-w-[105px] whitespace-nowrap sm:border-l border-slate-200 dark:border-slate-800 sm:shadow-2xs">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200/80 dark:divide-slate-800/80 text-sm">
                {staffList.map((member) => {
                  return (
                    <tr
                      key={member.uuid}
                      className="group hover:bg-slate-50/80 dark:hover:bg-slate-800/60 transition-all"
                    >
                      {/* Combined Sticky Staff Member Cell */}
                      <td className="py-3 px-3 pl-3 sm:sticky sm:left-0 z-10 min-w-[260px] transition-colors sm:border-r-2 sm:border-slate-300 dark:sm:border-slate-700 sm:shadow-[6px_0_16px_-3px_rgba(0,0,0,0.08)] dark:sm:shadow-[6px_0_16px_-3px_rgba(0,0,0,0.4)] bg-white dark:bg-slate-900 group-hover:bg-slate-50 dark:group-hover:bg-slate-800">
                        <StaffIdentity member={member} />
                      </td>

                      {/* The present address. Masked for anyone who may not change it. */}
                      <td className="py-2.5 px-3 min-w-[200px] max-w-[260px]">
                        {member.address ? (
                          <span className="line-clamp-2 text-xs font-medium text-slate-600 dark:text-slate-300" title={member.address}>
                            {member.address}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs font-semibold">—</span>
                        )}
                      </td>

                      {/* Each class they take and what they teach in it, so the
                          office sees *where* as well as *what*. */}
                      <td className="py-2.5 px-3 min-w-[220px]">
                        {member.classes && member.classes.length > 0 ? (
                          <ClassAllotmentList classes={member.classes} staffName={member.fullName} />
                        ) : member.subjects && member.subjects.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-1 max-w-[260px]">
                            {member.subjects.map((subject) => (
                              <span
                                key={subject}
                                className="inline-flex items-center gap-1 rounded-md bg-indigo-500/10 dark:bg-indigo-500/20 px-1.5 py-0.5 text-[10.5px] font-bold text-indigo-700 dark:text-indigo-300 border border-indigo-500/25 shadow-2xs"
                                title={`Teaching: ${subject}`}
                              >
                                <BookOpen className="h-2.5 w-2.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
                                <span className="truncate max-w-[120px]">{subject}</span>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs font-semibold">—</span>
                        )}
                      </td>

                      {/* Where they are this period and where they go next, today,
                          from the published timetable. */}
                      <td className="py-2.5 px-3 min-w-[190px]">
                        <LessonNowNext
                          now={member.nowTeaching}
                          next={member.nextClass}
                          teaches={Boolean(member.subjects?.length)}
                        />
                      </td>

                      {/* The salary in force today, from payroll. */}
                      {canViewPayroll && (
                        <td className="py-2.5 px-2.5">
                          {member.salary ? (
                            <div>
                              <div className="font-extrabold text-slate-700 dark:text-white text-base tabular-nums">
                                {formatCurrency(member.salary.netPay)}
                                <span className="ml-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">net</span>
                              </div>
                              <div className="text-xs text-slate-600 dark:text-slate-400 font-medium leading-snug space-y-0.5 tabular-nums">
                                <div>Basic: {formatCurrency(member.salary.basic)}</div>
                                <div>Allow: {formatCurrency(member.salary.allowances)}</div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm font-semibold text-slate-400">Not set</span>
                          )}
                        </td>
                      )}

                      {/* Approved leave days, counted by the server for this page's
                          rows. "—" where there is nothing to count against: no
                          staff profile, or no current session for the total. */}
                      <td className="py-2.5 px-2.5 text-center">
                        <div className="grid grid-cols-2 gap-1 px-1 text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-200">
                          <span>{formatNumber(member.leaveDaysThisMonth)}</span>
                          <span>{formatNumber(member.leaveDaysThisSession)}</span>
                        </div>
                      </td>

                      {canViewPayroll && (
                        <td className="py-2.5 px-2.5">
                          {member.salary?.lastIncrement ? (
                            <>
                              {member.salary.lastIncrement.percent !== null && (
                                <div className="font-extrabold text-emerald-600 dark:text-emerald-400 text-sm">
                                  {describeIncrement(member.salary.lastIncrement.percent)}
                                </div>
                              )}
                              <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                {formatDate(member.salary.lastIncrement.on)}
                              </div>
                            </>
                          ) : (
                            <span className="text-sm font-semibold text-slate-400">—</span>
                          )}
                        </td>
                      )}

                      {/* Status */}
                      <td className="py-2.5 px-2.5">
                        {member.invitePending ? (
                          <button
                            type="button"
                            onClick={() => resendInvite(member)}
                            disabled={invitingUuid === member.uuid}
                            title="Click to resend invitation email"
                            className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-extrabold bg-amber-100 dark:bg-amber-950/80 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-800 uppercase tracking-wider hover:bg-amber-200 dark:hover:bg-amber-900 transition-colors disabled:opacity-50 cursor-pointer"
                          >
                            {invitingUuid === member.uuid ? 'SENDING...' : 'INVITE PENDING'}
                          </button>
                        ) : (
                          <Badge tone={STAFF_STATUS_TONE[member.status]}>
                            {STAFF_STATUS_LABEL[member.status]}
                          </Badge>
                        )}
                      </td>

                      {/* Actions Column (Sticky Right 0 with vertical border & elevation shadow on sm+) */}
                      <td className="py-2.5 px-2 pr-3 text-right sm:sticky sm:right-0 transition-colors sm:border-l-2 sm:border-slate-300 dark:sm:border-slate-700 sm:shadow-[-6px_0_16px_-3px_rgba(0,0,0,0.08)] dark:sm:shadow-[-6px_0_16px_-3px_rgba(0,0,0,0.4)] z-10 bg-white dark:bg-slate-900 group-hover:bg-slate-50 dark:group-hover:bg-slate-800">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewingProfileMember(member);
                            }}
                            className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/60 transition-colors cursor-pointer rounded-lg border-0 bg-transparent"
                            title={`View ${member.fullName} profile`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openForm(member);
                            }}
                            className="p-1.5 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/60 transition-colors cursor-pointer rounded-lg border-0 bg-transparent"
                            title={`Edit ${member.fullName}`}
                          >
                            <PencilLine className="w-4 h-4" />
                          </button>

                          {/* The shared menu: drawn above the page, so the table's
                              scroll box can no longer clip it, and it flips above
                              the button when the row is near the bottom. */}
                          <Menu
                            label={`More actions for ${member.fullName}`}
                            trigger={<MoreVertical className="w-4 h-4" aria-hidden />}
                            triggerClassName="p-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer rounded-lg border-0 bg-transparent"
                            items={[
                              {
                                key: 'profile',
                                label: 'View profile',
                                icon: <Eye className="w-4 h-4" />,
                                onSelect: () => setViewingProfileMember(member),
                              },
                              {
                                key: 'edit',
                                label: 'Edit staff member',
                                icon: <PencilLine className="w-4 h-4" />,
                                onSelect: () => openForm(member),
                              },
                              ...(TEACHING_ROLES.includes(member.staffType)
                                ? [
                                    {
                                      key: 'allot',
                                      // One screen for both: it opens on what they already take.
                                      label: 'Assign or edit classes',
                                      icon: <GraduationCap className="w-4 h-4" />,
                                      onSelect: () => setAllotting(member),
                                    },
                                  ]
                                : []),
                              ...(member.invitePending
                                ? [
                                    {
                                      key: 'invite',
                                      label: 'Resend Invitation',
                                      icon: <RotateCcw className="w-4 h-4" />,
                                      onSelect: () => void resendInvite(member),
                                    },
                                  ]
                                : []),
                              member.status === 'ACTIVE'
                                ? {
                                    key: 'deactivate',
                                    label: 'Suspend Account',
                                    icon: <Ban className="w-4 h-4" />,
                                    tone: 'danger' as const,
                                    onSelect: () => setStatusTarget(member),
                                  }
                                : {
                                    key: 'reactivate',
                                    label: 'Reactivate Account',
                                    icon: <UserCheck className="w-4 h-4" />,
                                    onSelect: () => setStatusTarget(member),
                                  },
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Dynamic Pagination based on data.meta */}
        {data?.meta && data.meta.total > 0 && (
          <Pagination
            meta={data.meta}
            itemLabel="staff members"
            showPageNumbers
            summaryTextClass="!text-sm"
            onPageChange={(page) => updateFilters({ page })}
          />
        )}
      </div>

      {/* Modals & Dialogs */}
      <Modal
        isOpen={isImporting}
        onClose={() => setIsImporting(false)}
        title="Import staff members from a spreadsheet"
        description="Bulk add teachers and staff members at once using an Excel or CSV template file."
        size="lg"
        footer={
          <button
            type="button"
            onClick={() => setIsImporting(false)}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-base font-bold tracking-wide transition-colors cursor-pointer"
          >
            Done
          </button>
        }
      >
        {isImporting && <BulkImportSteps config={STAFF_IMPORT} onImported={() => reload()} />}
      </Modal>

      {allotting && (
        <ClassAllotmentModal
          staff={allotting}
          isOpen
          onClose={() => setAllotting(null)}
          onSaved={(allocations) => {
            announceAllotmentSaved(allotting.fullName, allocations);
            setAllotting(null);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(statusTarget)}
        tone={statusTarget?.status === 'ACTIVE' ? 'danger' : undefined}
        title={
          statusTarget?.status === 'ACTIVE' ? (
            <>
              Suspend <span className="inline-block px-1.5 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-bold">{statusTarget.fullName}</span>?
            </>
          ) : (
            <>
              Reactivate <span className="inline-block px-1.5 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-bold">{statusTarget?.fullName ?? 'this account'}</span>?
            </>
          )
        }
        description={
          statusTarget?.status === 'ACTIVE'
            ? 'They are signed out immediately and cannot sign in until reactivated. They stay on the staff list as suspended, with their record and classes kept.'
            : 'They can sign in again straight away, with the role and classes they had.'
        }
        confirmLabel={statusTarget?.status === 'ACTIVE' ? 'Suspend' : 'Reactivate'}
        isLoading={isChangingStatus}
        onConfirm={confirmStatusChange}
        onCancel={() => setStatusTarget(null)}
      />
    </div>
  );
}

/**
 * The payroll tile's figures for the current month, from the payroll records.
 *
 * Each state says what it is rather than showing a number it does not have:
 * without `PAYROLL_VIEW` there is nothing to show, and with no salary set up
 * there is nothing to add up — a ₹0 would read as "nobody is owed anything".
 */
function MonthPayrollFigures({
  canView,
  summary,
  isLoading,
  hasError,
}: {
  canView: boolean;
  summary: MonthPayrollSummary | null;
  isLoading: boolean;
  hasError: boolean;
}) {
  const note = 'text-[11px] font-medium text-teal-700/80 dark:text-teal-400/80 mt-0.5 block';

  if (!canView) {
    return (
      <>
        <div className="text-2xl font-black text-slate-400 mt-1">—</div>
        <span className={note}>Needs payroll access</span>
      </>
    );
  }
  if (isLoading) {
    return (
      <>
        <div className="text-2xl font-black text-slate-400 mt-1 animate-pulse" aria-label="Loading">…</div>
        <span className={note}>Loading this month</span>
      </>
    );
  }
  if (hasError || !summary) {
    return (
      <>
        <div className="text-2xl font-black text-slate-400 mt-1">—</div>
        <span className={note}>Could not load this month’s figures</span>
      </>
    );
  }
  if (summary.salariedCount === 0) {
    return <span className={cn(note, 'mt-1.5')}>Set up salaries to see this month’s payroll</span>;
  }
  return (
    <>
      <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">
        {formatCurrency(summary.due)}
      </div>
      <div className="text-[11px] font-semibold text-teal-700/90 dark:text-teal-400/90 mt-0.5 block truncate tabular-nums">
        <span className="text-teal-700 dark:text-teal-400">Paid: {formatCurrency(summary.paid)}</span>{' '}
        <span className="text-rose-600 dark:text-rose-400 font-bold">• Due: {formatCurrency(summary.outstanding)}</span>
      </div>
    </>
  );
}

/** How many classes a row shows before folding the rest into "+N more". */
const CLASSES_SHOWN = 3;

/**
 * The Classes & subjects cell: one compact row per class — a bold class tag,
 * what they teach there, and a gold mark on the class they are class teacher of.
 *
 * Capped at three so a teacher with eight sections does not make their row
 * eight lines tall; the rest are named in the "+N more" tooltip.
 */
function ClassAllotmentList({ classes, staffName }: { classes: StaffClassAllotment[]; staffName?: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const shown = classes.slice(0, CLASSES_SHOWN);
  const hidden = classes.slice(CLASSES_SHOWN);

  return (
    <>
      <ul className="flex flex-col gap-1.5 max-w-[300px]">
        {shown.map((allotment) => (
          <li
            key={allotment.displayName}
            className={cn(
              'flex items-center gap-2 rounded-lg border px-1.5 py-1',
              allotment.isClassTeacher
                ? 'border-amber-200 bg-amber-50/70 dark:border-amber-800/70 dark:bg-amber-950/30'
                : 'border-slate-200 bg-slate-50/70 dark:border-slate-700/80 dark:bg-slate-800/40',
            )}
          >
            <span
              className="shrink-0 rounded-md bg-indigo-600 px-1.5 py-0.5 text-[11px] font-black leading-tight text-white whitespace-nowrap shadow-2xs"
              title={allotment.displayName}
            >
              {allotment.displayName.replace(/^class\s+/i, '')}
            </span>

            <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-700 dark:text-slate-200">
              {allotment.subjects.length > 0 ? (
                allotment.subjects.map(capitalizeWords).join(' · ')
              ) : (
                <span className="font-medium italic text-slate-400">Register only</span>
              )}
            </span>

            {allotment.isClassTeacher && (
              <span
                className="inline-flex shrink-0 items-center gap-0.5 rounded-md bg-amber-500 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-white"
                title={`Class teacher of ${allotment.displayName}`}
              >
                <Crown className="h-2.5 w-2.5" aria-hidden />
                CT
              </span>
            )}
          </li>
        ))}

        {hidden.length > 0 && (
          <li>
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              className="pl-1 text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:underline cursor-pointer transition-colors"
            >
              +{hidden.length} more {hidden.length === 1 ? 'class' : 'classes'}
            </button>
          </li>
        )}
      </ul>

      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={staffName ? `Assigned Classes — ${staffName}` : 'Assigned Classes & Subjects'}
          description={`${classes.length} ${classes.length === 1 ? 'class' : 'classes'} allotted in the current academic year.`}
          size="sm"
        >
          <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto pr-1">
            {classes.map((allotment) => (
              <div
                key={allotment.displayName}
                className={cn(
                  'flex items-center gap-2.5 rounded-xl border p-2.5 transition-all',
                  allotment.isClassTeacher
                    ? 'border-amber-200 bg-amber-50/70 dark:border-amber-800/70 dark:bg-amber-950/30'
                    : 'border-slate-200 bg-slate-50/70 dark:border-slate-700/80 dark:bg-slate-800/40',
                )}
              >
                <span
                  className="shrink-0 rounded-lg bg-indigo-600 px-2 py-1 text-xs font-black leading-tight text-white whitespace-nowrap shadow-2xs"
                  title={allotment.displayName}
                >
                  {allotment.displayName}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">
                    {allotment.subjects.length > 0 ? (
                      allotment.subjects.map(capitalizeWords).join(' · ')
                    ) : (
                      <span className="font-medium italic text-slate-400">Register only (No subjects)</span>
                    )}
                  </p>
                </div>

                {allotment.isClassTeacher && (
                  <span
                    className="inline-flex shrink-0 items-center gap-1 rounded-md bg-amber-500 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-white shadow-2xs"
                    title={`Class teacher of ${allotment.displayName}`}
                  >
                    <Crown className="h-3 w-3" aria-hidden />
                    Class Teacher
                  </span>
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
}
